/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  HostPortPath,
  HttpStatusCode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  RouteDefinition,
  useBankCoreApiContext,
  useNotificationContext,
  useNavigationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, Fragment, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useWithdrawalDetails } from "../hooks/account.js";
import { useBankState } from "../hooks/bank-state.js";
import { LoggedIn, useSessionState } from "../hooks/session.js";
import { NeedConfirmationView } from "./OperationState/views.js";
import { AbortWithdrawalDialog } from "../components/AbortWithdrawalDialog.js";
import {
  buildWithdrawalOperationState,
  isTerminalWithdrawalResult,
} from "./OperationState/state.js";

interface ActiveWithdrawalProps {
  username: string;
  openOperationId?: string;
  routeOperation: RouteDefinition<{ wopid: string }>;
  routeClose: RouteDefinition;
  children: ComponentChildren;
}

export function ActiveWithdrawal({
  username,
  openOperationId,
  routeOperation,
  routeClose,
  children,
}: ActiveWithdrawalProps): VNode {
  const { i18n } = useTranslationContext();
  const { navigateTo } = useNavigationContext();
  const [bankState, updateBankState] = useBankState();
  const {
    url: backendUrl,
    lib: { bank },
  } = useBankCoreApiContext();
  const active = bankState.activeWithdrawal;
  const activeMatches =
    active?.username === username && active.backendBaseUrl === backendUrl.href;
  const operationId = activeMatches ? active.operationId : undefined;
  const operationRouteOpen = openOperationId !== undefined;
  const routeHasConflict =
    activeMatches &&
    openOperationId !== undefined &&
    openOperationId !== operationId;
  const watchedOperationId = operationRouteOpen ? undefined : operationId;
  const polledOperationId = useRef(watchedOperationId);
  const polledResult = useWithdrawalDetails(watchedOperationId);
  const result =
    polledOperationId.current === watchedOperationId ? polledResult : undefined;
  polledOperationId.current = watchedOperationId;

  useEffect(() => {
    if (active || !bankState.currentWithdrawalOperationId) return;
    updateBankState("activeWithdrawal", {
      operationId: bankState.currentWithdrawalOperationId,
      username,
      backendBaseUrl: backendUrl.href,
      confirmationDeferred: false,
      kind: "standard",
    });
  }, [
    active,
    backendUrl.href,
    bankState.currentWithdrawalOperationId,
    updateBankState,
    username,
  ]);

  useEffect(() => {
    if (!active || activeMatches) return;
    updateBankState("activeWithdrawal", undefined);
  }, [active, activeMatches, updateBankState]);

  useEffect(() => {
    if (
      active ||
      bankState.currentWithdrawalOperationId ||
      openOperationId === undefined
    ) {
      return;
    }
    updateBankState("activeWithdrawal", {
      operationId: openOperationId,
      username,
      backendBaseUrl: backendUrl.href,
      confirmationDeferred: false,
      kind: "standard",
    });
  }, [
    active,
    backendUrl.href,
    bankState.currentWithdrawalOperationId,
    openOperationId,
    updateBankState,
    username,
  ]);

  useEffect(() => {
    if (!routeHasConflict || !operationId) return;
    navigateTo(routeOperation.url({ wopid: operationId }));
  }, [navigateTo, operationId, routeHasConflict, routeOperation]);

  useEffect(() => {
    if (!activeMatches || !result || !isTerminalWithdrawalResult(result)) {
      return;
    }
    updateBankState("activeWithdrawal", undefined);
    navigateTo(routeClose.url({}));
  }, [activeMatches, navigateTo, result, routeClose, updateBankState]);

  const clear = (): void => updateBankState("activeWithdrawal", undefined);
  const finish = (): void => {
    clear();
    navigateTo(routeClose.url({}));
  };
  const defer = (): void => {
    if (!active) return;
    updateBankState("activeWithdrawal", {
      ...active,
      confirmationDeferred: true,
    });
  };

  let selectedDialog: VNode | undefined;
  let status: "checking" | "pending" | "selected" | "error" = "checking";
  if (result instanceof TalerError) {
    status = "error";
  } else if (result?.type === "ok") {
    status =
      result.body.status === "selected"
        ? "selected"
        : result.body.status === "pending"
          ? "pending"
          : "checking";
    if (
      status === "selected" &&
      active &&
      !active.confirmationDeferred &&
      !operationRouteOpen
    ) {
      const state = buildWithdrawalOperationState({
        result,
        operationId: active.operationId,
        bankIntegrationApiBaseUrl: bank.getIntegrationAPI()
          .href as HostPortPath,
        routeClose,
        onAbort: finish,
        onContinueLater: defer,
        loadingErrorTitle: i18n.str`Failed to load withdrawal details.`,
        onRetry: () => undefined,
      });
      if (state.status === "need-confirmation") {
        selectedDialog = <NeedConfirmationView {...state} />;
      }
    }
  }

  const terminal = result !== undefined && isTerminalWithdrawalResult(result);
  const showNotice = !!operationId && !operationRouteOpen && !terminal;

  return (
    <Fragment>
      {showNotice ? (
        <ActiveWithdrawalNotice
          operationId={operationId}
          status={status}
          routeOperation={routeOperation}
          onReview={() => {
            if (!active) return;
            updateBankState("activeWithdrawal", {
              ...active,
              confirmationDeferred: false,
            });
          }}
          onAborted={finish}
        />
      ) : undefined}
      {routeHasConflict ? undefined : children}
      {selectedDialog}
    </Fragment>
  );
}

export function ActiveWithdrawalNotice({
  operationId,
  status,
  routeOperation,
  onReview,
  onAborted,
}: {
  operationId: string;
  status: "checking" | "pending" | "selected" | "error";
  routeOperation: RouteDefinition<{ wopid: string }>;
  onReview(): void;
  onAborted(): void;
}): VNode {
  const { i18n } = useTranslationContext();
  const { showError } = useNotificationContext();
  const [confirmAbort, setConfirmAbort] = useState(false);
  const abortTriggerRef = useRef<HTMLButtonElement>(null);
  const { state: credentials } = useSessionState();
  const creds = credentials.status === "loggedIn" ? credentials : undefined;
  const {
    lib: { bank },
  } = useBankCoreApiContext();

  const abort = useNotifiedOperation<
    Awaited<ReturnType<typeof bank.abortWithdrawalById>>,
    [LoggedIn]
  >((ct, auth: LoggedIn) => bank.abortWithdrawalById(auth, operationId), {
    onSuccess: () => {
      setConfirmAbort(false);
      onAborted();
    },
    onFail: showError(i18n.str`Failed to abort the withdrawal.`, (fail) => {
      switch (fail.case) {
        case HttpStatusCode.BadRequest:
          return i18n.str`The server did not understand the request.`;
        case HttpStatusCode.NotFound:
          return i18n.str`The operation was not found.`;
        case HttpStatusCode.Conflict:
          return i18n.str`The withdrawal operation has been confirmed previously and can not be aborted.`;
        default:
          assertUnreachable(fail);
      }
    }),
  });

  const title =
    status === "selected"
      ? i18n.str`Withdrawal ready for confirmation`
      : status === "pending"
        ? i18n.str`Withdrawal waiting for your wallet`
        : status === "error"
          ? i18n.str`The pending withdrawal could not be checked`
          : i18n.str`Checking the pending withdrawal…`;
  const action =
    status === "selected"
      ? i18n.str`Review withdrawal`
      : status === "error"
        ? i18n.str`Retry`
        : i18n.str`Resume withdrawal`;
  const needsAttention = status === "selected";

  return (
    <Fragment>
      <section
        class={`mb-4 flex flex-col gap-3 rounded-lg border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between ${
          needsAttention
            ? "border-warning/60 bg-warningContainer text-onWarningContainer"
            : "border-primary/30 bg-primary/10 text-onBackground"
        }`}
        aria-labelledby="active-withdrawal-title"
        role={needsAttention ? "alert" : "status"}
      >
        <div>
          <h2 id="active-withdrawal-title" class="font-semibold text-inherit">
            {title}
          </h2>
          <p class="mt-1 text-inherit opacity-80">
            <i18n.Translate>
              Finish or abort this withdrawal before starting another one.
            </i18n.Translate>
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <button
            ref={abortTriggerRef}
            type="button"
            class="text-sm font-semibold text-red-700 hover:text-red-800"
            onClick={() => setConfirmAbort(true)}
          >
            <i18n.Translate>Abort withdrawal</i18n.Translate>
          </button>
          <a
            href={routeOperation.url({ wopid: operationId })}
            class="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white"
            onClick={onReview}
          >
            {action}
          </a>
        </div>
      </section>
      {confirmAbort ? (
        <AbortWithdrawalDialog
          running={abort.running}
          disabled={!creds}
          returnFocus={abortTriggerRef}
          onKeep={() => setConfirmAbort(false)}
          onAbort={() => abort.run(creds!)}
        />
      ) : undefined}
    </Fragment>
  );
}
